import { BrandSymbolKey, FlagIcon, FlagKey, IconKey } from '@melio/penny';
import {
  Card,
  DeliveryMethod,
  DeliveryMethodType,
  InternationalAccountBicSwift,
  InternationalAccountDeliveryMethod,
  InternationalAccountIban,
} from '@melio/platform-api';

const isIban = (
  details: InternationalAccountBicSwift | InternationalAccountIban
): details is InternationalAccountIban => details.identifierType === 'iban';

const getInternationalIdentifierData = (details: InternationalAccountBicSwift | InternationalAccountIban) => {
  if (isIban(details)) {
    return {
      identifierType: details.identifierType,
      identifierValue: details.identifierDetails.iban,
    };
  } else {
    return {
      identifierType: 'swift', // casting api 'bicSwift' type to app 'swift' type
      identifierValue: details.identifierDetails.bicSwift,
    };
  }
};

export const getInternationalFormattedIdentifierData = (
  deliveryMethod: InternationalAccountDeliveryMethod,
  countryDisplayName?: string
): { identifierType: string; identifierValue: string; flag: JSX.Element | '' } => {
  const { identifierType, identifierValue } = getInternationalIdentifierData(deliveryMethod.details);
  const flagType = countryDisplayName?.toLowerCase().replace(' ', '-');
  const flag = flagType ? <FlagIcon type={flagType as FlagKey} size="small" /> : '';

  return {
    identifierType: identifierType.toUpperCase(),
    identifierValue: identifierValue.slice(-4),
    flag,
  };
};

export const isInternationalDeliveryMethod = (
  deliveryMethod: DeliveryMethod | undefined
): deliveryMethod is InternationalAccountDeliveryMethod => deliveryMethod?.type === 'international-account';

export const getDeliveryMethodIconKey = (
  deliveryMethod: DeliveryMethod,
  icons: { [key: string]: BrandSymbolKey }
): IconKey | BrandSymbolKey => {
  switch (deliveryMethod.type) {
    case 'bank-account':
      return 'bank';
    case 'paper-check':
      return 'paper-check';
    case 'domestic-wire-account':
      return 'bank';
    case 'virtual-account':
      return 'email';
    case 'virtual-card':
      return icons['virtualCard'] as BrandSymbolKey;
    case 'biller-account':
    case 'managed-account':
      return 'directory-payment';
    case 'international-account':
      return 'international';
    case 'card': {
      return (deliveryMethod.details as Card).type === 'debit' ? 'debit-card' : 'credit-card';
    }
  }
};

export const getIsChangingDeliveryMethodIsDisabled = (existingDeliveryMethod: DeliveryMethod) =>
  existingDeliveryMethod.isManaged || existingDeliveryMethod.type === DeliveryMethodType.InternationalAccount;
